import React, { useRef, useState, useEffect } from "react";
import { useMixpanel } from "../../../Context/MixpanelContext/MixpanelProvider";
import {
  IonModal,
  IonContent,
  IonHeader,
  IonToolbar,
  IonRadio,
  IonRadioGroup,
} from "@ionic/react";
import { useSelector } from "react-redux";
import { actions } from "../../../Redux/store";
import { useNavigate } from "react-router-dom";
import {
  getLeague,
  getSlepperUserInfo,
} from "../../../Controllers/LeagueSyncApi";
import { hapticsImpactLight } from "../../../lib/utils";
import LeagueSelectionSkeleton from "./LeagueSelectionSkeleton";

interface LeagueSelectionModal {
  isLeagueSelectionModal: boolean;
  toggleLeagueSelectionModal: () => void;
}

export default function LeagueSelectionModal() {
  const Mixpanel = useMixpanel();
  const [selectedLeague, setSelectedLeague] = useState<any>(null);
  const [leagueData, setLeagueData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isLeagueSelectionModal = useSelector(
    (sate: any) => sate.modal.leagueSelectionModal?.open,
  );

  const leagueSelectionModalData = useSelector(
    (sate: any) => sate.modal.leagueSelectionModal?.data?.data,
  );

  console.log(leagueSelectionModalData?.provider, "League");

  useEffect(() => {
    if (isLeagueSelectionModal && leagueSelectionModalData) {
      if (leagueSelectionModalData?.provider === "SLEEPER") {
        fetchSleeperLeagueData();
      }
    }
  }, [isLeagueSelectionModal, leagueSelectionModalData]);

  const [sleeperUserInfo, setSleeperUserInfo] = useState(null);
  const [isLoadingSleeperInfo, setIsLoadingSleeperInfo] = useState(false);
  const [getLeagueError, setGetLeagueError] = useState<string | null>(null);

  const fetchSleeperLeagueData = async () => {
    setIsLoadingSleeperInfo(true);
    try {
      const response = await getSlepperUserInfo(
        leagueSelectionModalData?.ownerUsename,
      );
      console.log("Sleeper User Info:", response);

      if (response.status === 200 && response.data) {
        setSleeperUserInfo(response.data);

        // After getting Sleeper user info, sync the league
        if (response.data?.user_id) {
          console.log("response.data.id", response.data.user_id);
          await getLeagueData(response.data.user_id);
        }
      } else {
        setSleeperUserInfo(null);
        setGetLeagueError(
          String(response.errors) || "Failed to fetch Sleeper user information",
        );
      }
    } catch (error) {
      setSleeperUserInfo(null);
      setGetLeagueError("Failed to fetch Sleeper user information");
      console.error("Error fetching Sleeper user info:", error);
    } finally {
      setIsLoadingSleeperInfo(false);
    }
  };

  const getLeagueData = async (sleeper_user_id = null) => {
    if (!leagueSelectionModalData) return;

    setIsLoading(true);
    setGetLeagueError(null);

    try {
      let requestBody = {};

      if (leagueSelectionModalData?.provider === "SLEEPER" && sleeper_user_id) {
        requestBody = {
          owner_id: sleeper_user_id,
        };
      }

      const response = await getLeague(
        leagueSelectionModalData?.provider,
        requestBody,
      );

      if (response.status === 200 && response.data) {
        setLeagueData(response.data);
      } else {
        setGetLeagueError(String(response.errors) || "Failed to sync league");
        setLeagueData([]);
      }
    } catch (error: any) {
      console.error("Error syncing league:", error);
      setGetLeagueError(
        error.response?.data?.message || "Failed to sync league",
      );
      setLeagueData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const navigate = useNavigate();
  const modal = useRef<HTMLIonModalElement>(null);

  const handleConfirmClick = (league: any) => {
    // If no league is selected, don't proceed
    if (!league) {
      return;
    }

    setSelectedLeague(league);
    hapticsImpactLight();
    // Mixpanel event for league selection
    if (Mixpanel && typeof Mixpanel.trackEvent === "function") {
      Mixpanel.trackEvent("league_sync_continue", {
        leagueId: league?.leagueId,
        leagueName: league?.name,
      });
    }

    // Add a small delay for better user experience
    setTimeout(() => {
      dismiss();
      toggleSleeperModal();
      toggleLeagueModal();
      toggleAddNewLeagueModal();
      toggleChatbotSearchModal();
      navigate("/sync-league", {
        state: {
          leagueId: league?.leagueId,
          provider: leagueSelectionModalData?.provider,
          ...(leagueSelectionModalData?.provider === "SLEEPER"
            ? {
                sleeperData: sleeperUserInfo,
              }
            : {}),
          ...(leagueSelectionModalData?.provider === "ESPN"
            ? {
                espnData: {
                  espn_swid: leagueSelectionModalData?.provider_user_id || null,
                  espn_s2: leagueSelectionModalData?.espn_s2 || null,
                },
              }
            : {}),
        },
      });
    }, 300); // 300ms delay for visual feedback before navigation
  };

  const closeLeagueSelectionModal = () => {
    actions.modal.closeLeagueSelectionModal(null);
    setSelectedLeague(null);
    setLeagueData([]);
    setIsLoading(false);
    setSleeperUserInfo(null);
    setIsLoadingSleeperInfo(false);
    setGetLeagueError(null);
  };

  const toggleChatbotSearchModal = () => {
    actions.modal.closeChatbotSearchModal(null);
  };

  const toggleSleeperModal = () => {
    actions.modal.closeSleeperModal(null);
  };

  const toggleLeagueModal = () => {
    actions.modal.closeLeagueModal(null);
  };

  const toggleAddNewLeagueModal = () => {
    actions.modal.closeAddNewLeagueModal(null);
  };

  function dismiss() {
    modal.current?.dismiss();
    closeLeagueSelectionModal();
  }

  const handleRadioChange = (event: any) => {
    Mixpanel.trackEvent("league_to_sync_selected", {
      name: "User selected a league to sync",
      league_name:event.target.value.name
    });
    hapticsImpactLight();
    setSelectedLeague(event.target.value);
  };

  const handleCardClick = (leagueCard: any) => {
    hapticsImpactLight();
    setSelectedLeague(leagueCard);
  };

  return (
    <IonModal
      expandToScroll={false}
      onDidDismiss={dismiss}
      className="dynamic-height-modal"
      isOpen={isLeagueSelectionModal}
      backdropDismiss={true}
      initialBreakpoint={1}
      breakpoints={[0, 1]}
      ref={modal}
    >
      <IonHeader className="ion-no-border modal-ion-header">
        <IonToolbar className="custom-toolbar">
          <div className="flex items-center justify-center w-full px-4 pt-8">
            <span className="text-white text-[20px] font-medium leading-[140%] text-center">
              Which league would you like to sync?
            </span>
          </div>
        </IonToolbar>
      </IonHeader>

      <div className="league-content-section relative">
        <div
          className="px-[24px]"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom) + 50px)",
          }}
        >
          {isLoadingSleeperInfo || isLoading ? (
            <LeagueSelectionSkeleton />
          ) : leagueData.length === 0 ? (
            <div className="p-4 mt-14 text-center text-white/80">
              <p className="font-medium">No league found</p>
            </div>
          ) : (
            <IonRadioGroup
              value={selectedLeague}
              onIonChange={handleRadioChange}
            >
              <div className="flex-col justify-center pt-6 pb-8 space-y-[20px] cursor-pointer">
                {leagueData.map((leagueCard: any, index: any) => (
                  <div
                    key={index}
                    className="flex items-center justify-between w-full p-3"
                    onClick={() => handleCardClick(leagueCard)}
                  >
                    <div className="flex-col space-y-[2px]">
                      <p className="text-white text-[16px] font-medium">
                        {leagueCard?.name}
                      </p>
                      <p className="text-white/70 text-[12px] font-normal">
                        {leagueCard?.description || "--"}
                      </p>
                    </div>
                    <div className="h-full flex items-center justify-center">
                      <IonRadio
                        class="custom-radio"
                        mode="md"
                        value={leagueCard}
                        aria-label="Custom checkbox"
                      ></IonRadio>
                    </div>
                  </div>
                ))}
              </div>
            </IonRadioGroup>
          )}
        </div>

        {!isLoadingSleeperInfo && !isLoading && leagueData.length > 0 && (
          <div
            className="fixed bottom-0 left-0 right-0 pt-2 bg-[#000513] flex items-center justify-center  z-10"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom) + 20px)",
            }}
          >
            <button
              className="w-[280px] h-[48px] flex justify-center items-center bg-white/15  rounded-[50px]"
              onClick={() => handleConfirmClick(selectedLeague)}
            >
              <span className="text-[12px] font-medium text-white">
                Continue
              </span>
            </button>
          </div>
        )}
      </div>
    </IonModal>
  );
}
